import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import type { MappeListEntry } from "../multiagent-types";
import { PlanThumb } from "../components/PlanThumb";

/**
 * MultiagentListPage — Studio-Dashboard (Variante B aus Handoff).
 *
 * Sprint M (2026-05-03): umgebaut auf Mappen-Cards. Eine Card pro Projektmappe;
 * Klick öffnet den zuletzt-bearbeiteten Run der Mappe direkt im Editor.
 */
export default function MultiagentListPage() {
  const [mappen, setMappen] = useState<MappeListEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api
      .mappenList()
      .then((res) => {
        if (mounted) setMappen(res.mappen);
      })
      .catch((e) => {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      mounted = false;
    };
  }, []);

  const sorted = useMemo(() => {
    if (!mappen) return null;
    return [...mappen].sort((a, b) => {
      const ta = a.lastRunUpdatedAt
        ? new Date(a.lastRunUpdatedAt).getTime()
        : 0;
      const tb = b.lastRunUpdatedAt
        ? new Date(b.lastRunUpdatedAt).getTime()
        : 0;
      if (ta !== tb) return tb - ta;
      return new Date(b.updated).getTime() - new Date(a.updated).getTime();
    });
  }, [mappen]);

  const continueMappe = sorted && sorted.length > 0 ? sorted[0] : null;
  const restMappen = sorted && sorted.length > 0 ? sorted.slice(1) : [];

  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);

  function openMappe(m: MappeListEntry) {
    if (m.lastRunId) {
      navigate(`/multiagent/${m.lastRunId}`);
    }
    // TODO Sprint M.6 — Mappe-Detail-Page für Mappen ohne Runs
  }

  return (
    <div
      className="w-full mx-auto"
      style={{ maxWidth: 1280, padding: "28px 36px" }}
    >
      {/* Header */}
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div
            style={{
              fontSize: 11,
              color: "var(--bd-text-3)",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Atelier · bidi takeoff
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 600,
              letterSpacing: -0.6,
              color: "var(--bd-text)",
              margin: 0,
              lineHeight: 1.15,
            }}
          >
            {greeting}, Frank.
          </h1>
          <div
            style={{
              fontSize: 14,
              color: "var(--bd-text-2)",
              marginTop: 6,
              lineHeight: 1.55,
            }}
          >
            {continueMappe ? (
              <>
                Du arbeitest gerade an{" "}
                <span
                  style={{
                    color: "var(--bd-ember-strong)",
                    fontWeight: 500,
                  }}
                >
                  {continueMappe.name}
                </span>
                .{" "}
                {restMappen.length > 0
                  ? `${restMappen.length} weitere Mappe${restMappen.length === 1 ? "" : "n"} im Atelier.`
                  : "Bereit für die nächste Mappe."}
              </>
            ) : (
              <>
                Noch keine Mappe angelegt — lad einen Plan hoch und die KI baut
                dir die Räume.
              </>
            )}
          </div>
        </div>
        <Link
          to="/multiagent/upload"
          className="no-underline"
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            background: "var(--bd-text)",
            color: "var(--bd-bg)",
            fontSize: 13,
            fontWeight: 600,
            display: "flex",
            gap: 6,
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Plus /> Neuer Plan
        </Link>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 18,
            padding: 12,
            borderRadius: 8,
            background: "oklch(0.96 0.04 25)",
            border: "1px solid oklch(0.85 0.10 30)",
            color: "oklch(0.42 0.14 25)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {sorted === null ? (
        <div style={{ color: "var(--bd-text-3)", padding: 24 }}>
          Lade Mappen…
        </div>
      ) : sorted.length === 0 ? (
        <div className="bd-glass" style={{ padding: 36, textAlign: "center" }}>
          <div
            style={{
              fontSize: 14,
              color: "var(--bd-text-2)",
              marginBottom: 14,
            }}
          >
            Noch keine Mappe in <code>.bidi-takeoff/mappen/</code>.
          </div>
          <Link
            to="/multiagent/upload"
            className="no-underline inline-flex items-center gap-1.5"
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              background: "var(--bd-ember)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Upload /> Ersten Plan hochladen
          </Link>
        </div>
      ) : (
        <>
          {/* Continue card — die letzte aktive Mappe */}
          {continueMappe && (
            <button
              type="button"
              onClick={() => openMappe(continueMappe)}
              className="bd-glass mb-6"
              style={{
                width: "100%",
                padding: 18,
                marginBottom: 24,
                display: "flex",
                gap: 18,
                alignItems: "center",
                cursor: continueMappe.lastRunId ? "pointer" : "default",
                background: "color-mix(in oklab, var(--bd-surface) 82%, transparent)",
                border: "1px solid var(--bd-line)",
                borderRadius: 12,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 10,
                  background: "var(--bd-plan-bg)",
                  border: "1px solid var(--bd-line)",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {continueMappe.lastRunId ? (
                  <PlanThumb
                    runId={continueMappe.lastRunId}
                    highlight
                    scale={0.6}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      color: "var(--bd-text-3)",
                    }}
                  >
                    leer
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--bd-text-3)",
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Weitermachen
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: "var(--bd-text)",
                    marginTop: 2,
                  }}
                >
                  {continueMappe.name}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bd-text-3)",
                    marginTop: 2,
                  }}
                >
                  {mappeStatsLabel(continueMappe)}
                  {continueMappe.lastRunUpdatedAt
                    ? ` · zuletzt ${formatRelative(continueMappe.lastRunUpdatedAt)}`
                    : ""}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    height: 4,
                    borderRadius: 3,
                    background: "var(--bd-surface2)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, continueMappe.runCount * 25)}%`,
                      height: "100%",
                      background: "var(--bd-ember)",
                      borderRadius: 3,
                    }}
                  />
                </div>
              </div>
              <span
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  background: "var(--bd-ember)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  flexShrink: 0,
                  opacity: continueMappe.lastRunId ? 1 : 0.4,
                }}
              >
                Öffnen <ChevR />
              </span>
            </button>
          )}

          {/* All mappen grid */}
          <div
            style={{
              fontSize: 11,
              color: "var(--bd-text-3)",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            Alle Mappen · {sorted.length}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 14,
            }}
          >
            {sorted.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => openMappe(m)}
                className="bd-glass"
                style={{
                  overflow: "hidden",
                  cursor: m.lastRunId ? "pointer" : "default",
                  display: "block",
                  color: "inherit",
                  transition:
                    "transform 0.15s ease-out, box-shadow 0.15s ease-out",
                  textAlign: "left",
                  padding: 0,
                  border: "1px solid var(--bd-line)",
                  borderRadius: 12,
                  background:
                    "color-mix(in oklab, var(--bd-surface) 82%, transparent)",
                }}
                onMouseEnter={(e) => {
                  if (!m.lastRunId) return;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 36px rgba(28,26,23,0.10), 0 1px 2px rgba(28,26,23,0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "";
                }}
              >
                <div
                  style={{
                    height: 120,
                    background: "var(--bd-plan-bg)",
                    position: "relative",
                    borderBottom: "1px solid var(--bd-line)",
                    overflow: "hidden",
                  }}
                >
                  {m.lastRunId ? (
                    <PlanThumb runId={m.lastRunId} />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        color: "var(--bd-text-3)",
                      }}
                    >
                      noch leer
                    </div>
                  )}
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      padding: "3px 8px",
                      background:
                        "color-mix(in oklab, var(--bd-surface) 88%, transparent)",
                      backdropFilter: "blur(8px)",
                      borderRadius: 5,
                      fontSize: 10,
                      fontWeight: 600,
                      color: "var(--bd-ember-strong)",
                      border: "1px solid var(--bd-line)",
                    }}
                  >
                    📁 {m.runCount} Run{m.runCount === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ padding: "14px 16px" }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--bd-text)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--bd-text-3)",
                      marginTop: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.client || m.address || "—"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      marginTop: 10,
                      fontSize: 11,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "var(--bd-text-3)",
                          fontSize: 9,
                          letterSpacing: 0.3,
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        Inhalt
                      </div>
                      <div
                        style={{
                          color: "var(--bd-text)",
                          fontWeight: 500,
                          marginTop: 1,
                          fontSize: 11,
                        }}
                      >
                        {mappeStatsLabel(m)}
                      </div>
                    </div>
                    {m.lastRunUpdatedAt && (
                      <div>
                        <div
                          style={{
                            color: "var(--bd-text-3)",
                            fontSize: 9,
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            fontWeight: 600,
                          }}
                        >
                          Geändert
                        </div>
                        <div
                          style={{
                            color: "var(--bd-text)",
                            fontWeight: 500,
                            marginTop: 1,
                            fontSize: 11,
                          }}
                        >
                          {formatRelative(m.lastRunUpdatedAt)}
                        </div>
                      </div>
                    )}
                    <div style={{ marginLeft: "auto", alignSelf: "center" }}>
                      <ChevR style={{ color: "var(--bd-text-3)" }} />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function greetingForHour(h: number): string {
  if (h < 5) return "Späte Stunde";
  if (h < 11) return "Guten Morgen";
  if (h < 14) return "Guten Tag";
  if (h < 18) return "Guten Nachmittag";
  return "Guten Abend";
}

/**
 * Sprint M — kompakter Stats-Text für eine Mappe-Card. Liefert eine Zeile
 * wie "4 Runs" oder später (Sprint X) "4 Etagen + 0 Schnitte".
 * Aktuell zeigen wir nur die Anzahl, weil planTyp pro Run hier noch nicht
 * verfügbar ist (würde extra-Fetch kosten). Folgt in Sprint X.
 */
function mappeStatsLabel(m: MappeListEntry): string {
  if (m.runCount === 0) return "leer";
  return `${m.runCount} Run${m.runCount === 1 ? "" : "s"}`;
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const dMin = Math.round((now - t) / 60000);
  if (dMin < 1) return "gerade eben";
  if (dMin < 60) return `vor ${dMin} Min.`;
  const dH = Math.round(dMin / 60);
  if (dH < 24) return `vor ${dH} Std.`;
  const dD = Math.round(dH / 24);
  if (dD < 7) return `vor ${dD} Tag${dD === 1 ? "" : "en"}`;
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ============================================================================
// Tiny inline SVG icons (avoid pulling in icon library for one screen)
// ============================================================================

function Plus({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ChevR({
  size = 14,
  style,
}: {
  size?: number;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function Upload({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 16V4" />
      <path d="M6 10l6-6 6 6" />
      <path d="M4 18v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    </svg>
  );
}
